import { type BillingRange, billingToday, parseBillingRange } from '@smart-link/shared';
import { format, subMonths } from 'date-fns';
import { zhCN } from 'date-fns/locale';
import { CalendarDays, Check, X } from 'lucide-react';
import { useState } from 'react';
import type { DateRange } from 'react-day-picker';
import {
  BILLING_PRESETS,
  type BillingPreset,
  calendarDate,
  presetRange,
  rangeLabel,
} from '../lib/billing';
import { Button } from './ui/button';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';

export function BillingDateRangePicker({
  range,
  preset,
  onChange,
}: {
  range: BillingRange;
  preset: BillingPreset;
  onChange: (range: BillingRange, preset: BillingPreset) => void;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DateRange | undefined>();
  const [draftPreset, setDraftPreset] = useState<BillingPreset>('custom');
  const [month, setMonth] = useState<Date>();
  const today = calendarDate(billingToday());
  const valid =
    draft?.from && draft.to
      ? parseBillingRange(format(draft.from, 'yyyy-MM-dd'), format(draft.to, 'yyyy-MM-dd'))
      : null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Tabs
        value={['week', 'fortnight', 'month'].includes(preset) ? preset : ''}
        onValueChange={(value) => {
          const selected = BILLING_PRESETS.find((item) => item.id === value);
          if (selected) onChange(presetRange(selected.id), selected.id);
        }}
      >
        <TabsList aria-label="用量周期">
          <TabsTrigger value="week">7 天</TabsTrigger>
          <TabsTrigger value="fortnight">14 天</TabsTrigger>
          <TabsTrigger value="month">30 天</TabsTrigger>
        </TabsList>
      </Tabs>
      <Popover
        open={open}
        onOpenChange={(next) => {
          if (next) {
            setDraft({ from: calendarDate(range.from), to: calendarDate(range.to) });
            setDraftPreset(preset);
            setMonth(subMonths(calendarDate(range.to), 1));
          }
          setOpen(next);
        }}
      >
        <PopoverTrigger asChild>
          <Button variant="outline" className="min-w-0 justify-start font-normal tabular-nums">
            <CalendarDays className="text-muted-foreground" />
            <span className="truncate">{rangeLabel(range.from, range.to)}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="max-h-[var(--radix-popover-content-available-height)] w-auto max-w-[calc(100vw-2rem)] overflow-auto p-0"
        >
          <div className="flex flex-col sm:flex-row">
            <div className="bg-muted/30 flex shrink-0 flex-wrap gap-1 border-b p-3 sm:w-32 sm:flex-col sm:border-r sm:border-b-0">
              {BILLING_PRESETS.map((item) => (
                <Button
                  key={item.id}
                  size="sm"
                  variant={draftPreset === item.id ? 'secondary' : 'ghost'}
                  className="justify-between"
                  onClick={() => {
                    const next = presetRange(item.id);
                    setDraft({ from: calendarDate(next.from), to: calendarDate(next.to) });
                    setDraftPreset(item.id);
                    setMonth(subMonths(calendarDate(next.to), 1));
                  }}
                >
                  {item.label}
                  {draftPreset === item.id && <Check className="size-3.5" />}
                </Button>
              ))}
            </div>
            <div>
              <Calendar
                mode="range"
                locale={zhCN}
                labels={{ labelPrevious: () => '上一个月', labelNext: () => '下一个月' }}
                weekStartsOn={1}
                numberOfMonths={2}
                month={month}
                onMonthChange={setMonth}
                selected={draft}
                onSelect={(value) => {
                  setDraft(value);
                  setDraftPreset('custom');
                }}
                disabled={{ after: today }}
                endMonth={today}
              />
              <div className="flex flex-wrap items-center justify-between gap-3 border-t p-3">
                <div className="text-muted-foreground text-xs" aria-live="polite">
                  {valid ? rangeLabel(valid.from, valid.to) : '最多 366 天'}
                  <span className="ml-2">UTC+8</span>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    title="取消"
                    aria-label="取消"
                    onClick={() => setOpen(false)}
                  >
                    <X />
                  </Button>
                  <Button
                    size="icon"
                    title="应用日期"
                    aria-label="应用日期"
                    disabled={!valid}
                    onClick={() => {
                      if (valid) {
                        onChange(valid, draftPreset);
                        setOpen(false);
                      }
                    }}
                  >
                    <Check />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
